import { useState, useEffect } from 'react';
import {
  Modal,
  Stack,
  TextInput,
  Group,
  Button,
  Text,
  List,
  ThemeIcon,
  Alert,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { Check, AlertCircle } from 'react-feather';
import { cohortsApi } from '../../../services/api/cohorts';
import { useFeatureFlag } from '../../../stores/featureFlags';
import type { CohortWithDetails } from '../../../types';

interface DuplicateCohortModalProps {
  opened: boolean;
  onClose: () => void;
  cohort: CohortWithDetails;
  onDuplicated: (newCohort: CohortWithDetails) => void;
}

export function DuplicateCohortModal({
  opened,
  onClose,
  cohort,
  onDuplicated,
}: DuplicateCohortModalProps) {
  const [duplicating, setDuplicating] = useState(false);
  // MED-1154 AC9: the "Modality configuration" copy is flag-aware. With Live
  // Session Delivery off (production today) we list only session count &
  // frequency and name no MED02 features; with it on, the duplicate also copies
  // coaching type and the commitment tracking / live session delivery / AI
  // features settings, so the copy expands to say so.
  const liveSessionDeliveryEnabled = useFeatureFlag('live_session_delivery');

  const form = useForm({
    initialValues: {
      name: `${cohort.name} (copy)`,
    },
  });

  // Reset form when modal opens with new cohort data
  useEffect(() => {
    if (opened) {
      form.setValues({
        name: `${cohort.name} (copy)`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opened, cohort.name]);

  const handleDuplicate = async () => {
    const values = form.values;

    // Validate
    if (!values.name.trim()) {
      form.setFieldError('name', 'Name is required');
      return;
    }

    try {
      setDuplicating(true);
      const newCohort = await cohortsApi.duplicate(cohort.id, values.name);
      notifications.show({
        title: 'Success',
        message: 'Cohort duplicated successfully',
        color: 'green',
      });
      onClose();
      onDuplicated(newCohort);
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: error instanceof Error ? error.message : 'Failed to duplicate cohort',
        color: 'red',
      });
    } finally {
      setDuplicating(false);
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Duplicate Cohort"
      size="md"
    >
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Create a copy of <strong>{cohort.name}</strong> with the same configuration.
        </Text>

        <TextInput
          label="New Cohort Name"
          placeholder="Enter a name for the duplicated cohort"
          required
          {...form.getInputProps('name')}
        />

        <Alert variant="light" color="blue" icon={<AlertCircle size={16} />}>
          <Text size="sm" fw={500} mb="xs">What will be copied:</Text>
          <List
            size="sm"
            spacing={4}
            icon={
              <ThemeIcon color="green" size={18} radius="xl">
                <Check size={12} />
              </ThemeIcon>
            }
          >
            <List.Item>Partner and program association</List.Item>
            <List.Item>
              {liveSessionDeliveryEnabled
                ? 'Modality configuration (session count, frequency, coaching type, and commitment tracking / live session delivery / AI features settings)'
                : 'Modality configuration (session count, frequency)'}
            </List.Item>
            <List.Item>Workshop definitions</List.Item>
            <List.Item>Scheduling preferences (preferred days/times)</List.Item>
          </List>
        </Alert>

        <Alert variant="light" color="orange" icon={<AlertCircle size={16} />}>
          <Text size="sm" fw={500} mb="xs">What will NOT be copied:</Text>
          <List
            size="sm"
            spacing={4}
            c="dimmed"
          >
            <List.Item>Start and end dates (will need to be set)</List.Item>
            <List.Item>Session schedules</List.Item>
            <List.Item>Participants and groups</List.Item>
            <List.Item>Team assignments</List.Item>
            <List.Item>Coach assignments</List.Item>
          </List>
        </Alert>

        <Text size="sm" c="dimmed">
          The new cohort will start in <strong>Draft</strong> status with a new auto-generated code.
        </Text>

        <Group justify="flex-end" className="modal-footer">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleDuplicate} loading={duplicating}>
            Duplicate Cohort
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
